<template>
  <div class="app-container">
    <div>
      <el-select
        v-model="selectedWorkshopID"
        class="search-select"
        type="select"
        style="margin-bottom: 10px"
        :placeholder="$t('common.workshop')"
        @input="handleWorkshopChange($event)"
      >
        <el-option v-for="workshop in workshops" :key="workshop.id" :label="workshop.name" :value="workshop.id" />
      </el-select>
      <el-select
        v-model="selectedDomainID"
        class="search-select"
        type="select"
        clearable
        style="margin-bottom: 10px; margin-left: 10px"
        :placeholder="$t('common.domain')"
        @change="handleDomainChange"
      >
        <el-option v-for="domain in domains" :key="domain.id" :label="domain.name" :value="domain.id" />
      </el-select>
      <el-date-picker
        v-model="selectedYear"
        type="year"
        value-format="yyyy"
        :clearable="false"
        :placeholder="$t('common.year')"
        style="margin-bottom: 10px; margin-left: 10px"
        @change="handleYearChange"
      />
      <el-select
        v-model="selectedWeeks"
        :multiple="true"
        class="search-select"
        type="select"
        style="margin-bottom: 10px; margin-left: 10px"
        :placeholder="$t('common.weeks')"
        @input="handleWeekChange($event)"
      >
        <el-option-group v-if="availableWeeks.length">
          <el-option v-for="week in availableWeeks" :key="week" :label="week" :value="week" />
        </el-option-group>
      </el-select>
      <el-input
        v-model="selectedSeries"
        :placeholder="$t('statusList.seriesPlaceholder')"
        style="margin-bottom: 10px; margin-left: 10px; width: 300px"
        pattern="^(\d{1,3}|\d{1,3}-\d{1,3})(,\d{1,3}|,\d{1,3}-\d{1,3})*$"
      >
      </el-input>
      <div style="float:right;">
        <editor-button
          :icon-only="false"
          :disabled="loading || selectedWorkshopID === null || selectedWeeks.length === 0"
          button-translation="common.createReport"
          action="primary"
          @click="getStatusList()"
        />
        <editor-button
          :icon-only="false"
          :disabled="loading || selectedWorkshopID === null || selectedWeeks.length === 0"
          button-translation="common.downloadExcel"
          action="primary"
          @click="downloadExcel()"
        />
      </div>
      <el-collapse-transition>
        <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" />
      </el-collapse-transition>
    </div>
    <div style="margin-bottom: 20px;">
      <el-radio-group v-model="report">
        <el-radio label="Scanned">{{ $t('statusList.scanned') }}</el-radio>
        <el-radio label="NotScanned">{{ $t('statusList.notScanned') }}</el-radio>
        <el-radio label="AllOrders">{{ $t('statusList.allOrders') }}</el-radio>
      </el-radio-group>
    </div>
    <ComponentListTotals :loading="loading" :series-product-types="seriesProductTypes" style="margin-bottom: 20px;" />
    <ComponentListExtras :loading="loading" :products="products" style="margin-bottom: 20px;" />
    <div>
      <pagination
        :page-sizes="[25, 50, 75, 100]"
        :total="loadedData.total"
        :page.sync="page"
        :limit.sync="pageSize"
        @pagination="updateStatusList"
      >
      </pagination>
      <el-table ref="statusList" v-loading="loading" :data="statusList" border fit>
        <el-table-column :label="$t('statusList.storeCode')" prop="storeCode">
          <template slot-scope="scope">
            <span>{{ scope.row['storeCode'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('statusList.storeName')" prop="storeName">
          <template slot-scope="scope">
            <span>{{ scope.row['storeName'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('statusList.seriesNumber')" prop="seriesNumber">
          <template slot-scope="scope">
            <span>{{ scope.row['seriesNumber'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('statusList.fabricCode')" prop="fabricCode">
          <template slot-scope="scope">
            <span>{{ scope.row['fabricCode'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('statusList.fabricName')" prop="fabricName">
          <template slot-scope="scope">
            <span>{{ scope.row['fabricName'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('statusList.ticketNumber')" prop="ticketNumber">
          <template slot-scope="scope">
            <span>{{ scope.row['ticketNumber'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('order.consumerName')" prop="consumerName">
          <template slot-scope="scope">
            <span>{{ scope.row['consumerName'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('statusList.hangers')">
          <template slot-scope="scope">
            <span>{{ scope.row['hangers'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('statusList.status')">
          <template slot-scope="scope">
            <span>{{ formatOrderLineStatus(scope.row, scope.column, scope.row['status']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('statusList.subStatus')">
          <template slot-scope="scope">
            <span>{{ scope.row['subStatus'] }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { ForceFileDownload } from '@/utils/general';
import EditorButton from '@/components/crud/EditorButton';
import Pagination from '@/components/Pagination';
import ComponentListExtras from '@/pages/planning/series/ComponentListExtras';
import ComponentListTotals from '@/pages/planning/series/ComponentListTotals';

export default {
  name: 'StatusList',
  components: {
    ComponentListExtras,
    ComponentListTotals,
    EditorButton,
    Pagination
  },
  data() {
    const workshops = this.$store.getters.workshops;

    return {
      statusList: [],
      loading: false,
      loadedData: { items: [], total: 0 },
      seriesProductTypes: [],
      products: [],
      report: 'NotScanned',
      errorMsg: '',
      selectedWorkshopID: workshops && workshops.length === 1 ? workshops[0].id : null,
      selectedDomainID: this.getCurrentDomainID(),
      selectedYear: new Date().getFullYear().toString(),
      availableWeeks: [],
      selectedWeeks: [],
      availableSeries: [],
      selectedSeries: '',
      page: 1,
      pageSize: 25
    };
  },
  computed: {
    listParams() {
      return {
        workshopID: this.selectedWorkshopID,
        domainID: this.selectedDomainID,
        year: this.selectedYear,
        weeks: this.selectedWeeks.join(','),
        series: this.selectedSeries,
        report: this.report,
        page: null,
        pageSize: null
      };
    },
    domains() {
      return this.$store.getters.domains;
    },
    workshops() {
      return this.$store.getters.workshops;
    }
  },
  created() {
    this.getAvailableWeeks();
  },
  methods: {
    getCurrentDomainID() {
      const currentDomainID = this.$store.getters['domain/getCurrentDomainID'];
      return currentDomainID && currentDomainID > 0 ? currentDomainID : null;
    },
    getStatusList() {
      this.refreshList();
    },
    refreshList() {
      this.loading = true;
      this.$store
        .dispatch('productionPlanningSeries/getStatusList', this.listParams)
        .then(data => {
          this.seriesProductTypes = data.componentList.seriesProductTypes;
          this.products = data.componentList.seriesProducts;
          this.loadedData.items = data.statusList.items;
          this.loadedData.total = data.statusList.total;
          this.updateStatusList();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatOrderLineStatus(row, column, cellValue) {
      const option =
        cellValue !== null &&
        this.$store.getters['option/loadedOptions']('orderLineStatus').find(item => item.code === cellValue);
      return option && option.description;
    },
    updateStatusList() {
      const loadedData = this.loadedData;
      if (loadedData && loadedData.items) {
        const pageSize = this.pageSize;
        const start = (this.page - 1) * pageSize;
        const end = start + pageSize;
        this.statusList = loadedData.items.slice(start, end);
      }
    },
    handleWorkshopChange() {
      this.getAvailableWeeks();
      this.getAvailableSeries();
    },
    handleDomainChange(domain) {
      this.getAvailableWeeks();
    },
    handleYearChange() {
      this.getAvailableWeeks();
    },
    handleWeekChange() {
      this.getAvailableSeries();
    },
    getAvailableWeeks() {
      if (this.selectedWorkshopID === null) {
        return;
      }
      const selectedWorkshopsAsString = this.selectedWorkshopID.toString();
      this.$store
        .dispatch('productionPlanningSeries/getAvailableWeeks', {
          workshopIDs: selectedWorkshopsAsString,
          domainID: this.selectedDomainID,
          year: this.selectedYear
        })
        .then(data => {
          this.availableWeeks = data.sort((a, b) => b - a); // Descending order
        });
    },
    getAvailableSeries() {
      if (this.selectedWorkshopID === null || this.selectedWeeks.length === 0) {
        return;
      }
      const selectedWorkshopsAsString = this.selectedWorkshopID.toString();
      const selectedWeeksAsString = this.selectedWeeks.join(',');
      this.$store
        .dispatch('productionPlanningSeries/getAvailableSeries', {
          workshopIDs: selectedWorkshopsAsString,
          year: this.selectedYear,
          weeks: selectedWeeksAsString
        })
        .then(data => {
          this.availableSeries = data;
        });
    },
    downloadExcel() {
      const params = this.listParams;
      delete params.page;
      delete params.pageSize;

      this.loading = true;
      this.$store
        .dispatch('productionPlanningSeries/downloadStatusList', params)
        .then(blob => {
          ForceFileDownload(blob, 'StatusList.xlsx');
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>
